import {
    ColDef,
    GridApi,
    ICellRendererParams,
    IDatasource,
    IGetRowsParams,
    RowSelectedEvent,
    SortModelItem,
} from '@ag-grid-community/core';
import { Box, Flex, Spinner, useBreakpointValue } from '@chakra-ui/react';
import { useMemo, useRef } from 'react';
import { useNavigate, useRouteLoaderData } from 'react-router-dom';
import { api } from '../../lib/api';
import { LoginResponseDto, PatientListItemDto } from '../../lib/ServerApi';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { AccessGuard } from '@/components/access-guard';
import { GridFilterActionBar } from '@/components/ui/grid-filter-bar';
import { LinkButton } from '@/components/ui/link-button';
import { ResponsiveGrid } from '@/components/ui/responsive-grid';
import { useFeatureFlag } from '@/providers/feature-flag-provider';
import { usePersistedGridState } from '@/hooks/usePersistedGridState';
import { capitalize } from '@/utils/capitalize';
import { StatusFilter, StatusFloatingFilter, STATUS_LABELS } from './status-filter';
import { branchColumnFilter, branchFilterValueMaps } from './branch-filter';

const PATIENTS_LIST_STORAGE_KEY = 'patientsFilterModel';

const DEFAULT_GRID_STATE = {
    filterModel: {
        status: { value: 'referral intake pending admitted' },
    },
    sortModel: [],
};

function LoadingCellRenderer(props: ICellRendererParams<PatientListItemDto>) {
    if (props.value !== undefined) {
        return props.value;
    }
    return (
        <Flex align="center" color="purple.400" h="full">
            <Spinner size="sm" borderWidth="2px" />
        </Flex>
    );
}

const defaultColumnDef: ColDef<PatientListItemDto> = {
    resizable: true,
    floatingFilter: true,
    sortable: true,
    filter: true,
};

function formatName(rawName: string) {
    const parts = rawName.split(',').map((part) => capitalize(part.trim().split(/\s+/).join(' ')));

    return parts.join(', ');
}

// Map status filter values to API statuses
function mapStatusFilterToStatuses(filterValue: string | null): string[] | undefined {
    if (!filterValue) {
        return undefined; // Use server default
    }

    const statusMap: Record<string, string[]> = {
        'referral intake pending admitted': ['REFERRAL', 'INTAKE', 'PENDING', 'ADMITTED'],
        referral: ['REFERRAL'],
        intake: ['INTAKE'],
        pending: ['PENDING'],
        admitted: ['ADMITTED'],
        transferred: ['TRANSFERRED'],
        discharged: ['DISCHARGED'],
        'non admit': ['NON_ADMIT'],
    };

    return statusMap[filterValue.toLowerCase()] || undefined;
}

function buildQueryParams(
    startRow: number,
    pageSize: number,
    sortModel: SortModelItem[],
    filterModel: Record<string, { filter?: unknown; value?: string; dateFrom?: unknown; dateTo?: unknown; type?: unknown }>,
) {
    const page = Math.floor(startRow / pageSize) + 1;

    const params: Record<string, unknown> = {
        page,
        pageSize,
    };

    // Handle sorting
    if (sortModel.length > 0) {
        const { colId, sort } = sortModel[0];
        params.sortField = colId;
        params.sortDirection = sort;
    }

    if (filterModel) {
        if (filterModel.name?.filter) {
            params.name = filterModel.name.filter;
        }

        // Branch filter (custom BranchFilter sends branchId)
        if (filterModel.branchName?.value) {
            params.branchId = filterModel.branchName.value;
        }

        if (filterModel.status?.value) {
            const statuses = mapStatusFilterToStatuses(filterModel.status.value);
            if (statuses) {
                params.statuses = statuses.join(',');
            }
        }

        if (filterModel.dob?.dateFrom) {
            params.dob = filterModel.dob.dateFrom;
        }

        if (filterModel.episodeNumber?.filter !== undefined) {
            params.episodeNumber = filterModel.episodeNumber.filter;
        }

        if (filterModel.admissionNumber?.filter !== undefined) {
            params.admissionNumber = filterModel.admissionNumber.filter;
        }

        if (filterModel.socDate?.dateFrom) {
            params.socDate = filterModel.socDate.dateFrom;
            params.socDateType = filterModel.socDate.type;
            if (filterModel.socDate.type === 'inRange' && filterModel.socDate.dateTo) {
                params.socDateTo = filterModel.socDate.dateTo;
            }
        }
    }

    return params;
}

export function PatientsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const session = useRouteLoaderData('root') as LoginResponseDto;
    const { isEnabled } = useFeatureFlag();
    const intakeDashboardEnabled = isEnabled('intake-dashboard');
    const gridApiRef = useRef<GridApi<PatientListItemDto> | null>(null);
    const { gridState, saveGridState } = usePersistedGridState(PATIENTS_LIST_STORAGE_KEY, DEFAULT_GRID_STATE);
    const isCompact = useBreakpointValue({ base: true, lg: false }, { ssr: false });

    const branches = useMemo(
        () => session.scope?.branches.map((branch) => ({ id: branch.id, name: branch.name })) || [],
        [session.scope?.branches],
    );

    const datasource: IDatasource = useMemo(
        () => ({
            getRows: async (params: IGetRowsParams) => {
                const pageSize = 50;
                const queryParams = buildQueryParams(params.startRow, pageSize, params.sortModel, params.filterModel);

                try {
                    const { data } = await api.patient.get(queryParams);

                    const rowsThisBlock = data.data || [];
                    params.successCallback(rowsThisBlock, data.meta.totalRecords);
                } catch {
                    params.failCallback();
                }
            },
        }),
        [],
    );

    const colDefs = useMemo<ColDef<PatientListItemDto>[]>(() => {
        return [
            {
                field: 'branchName',
                headerName: 'Branch',
                flex: 1,
                minWidth: 200,
                ...branchColumnFilter(branches),
                cellRenderer: LoadingCellRenderer,
            },
            {
                field: 'name',
                headerName: 'Name',
                flex: 1.5,
                minWidth: 210,
                filter: 'agTextColumnFilter',
                valueGetter: (params) => {
                    const rawName = params.data?.name || '';
                    return formatName(rawName);
                },
            },
            {
                field: 'primaryPhone',
                headerName: 'Contact',
                width: 140,
                minWidth: 140,
                filter: 'agTextColumnFilter',
                sortable: false,
                valueGetter: (params) => params.data?.primaryPhone || (params.node?.id ? '-' : ''),
            },
            {
                field: 'dob',
                headerName: 'Date of Birth',
                width: 135,
                minWidth: 135,
                filter: 'agDateColumnFilter',
            },
            {
                colId: 'status',
                field: 'status',
                headerName: 'Status',
                width: 135,
                minWidth: 135,
                filter: StatusFilter,
                filterParams: { intakeDashboardEnabled },
                floatingFilterComponent: StatusFloatingFilter,
                valueGetter: (params) => {
                    return params.data ? t(`PATIENT.STATUS.${params.data.status}`) : '';
                },
            },
            {
                field: 'socDate',
                headerName: 'SOC Date',
                width: 135,
                minWidth: 135,
                filter: 'agDateColumnFilter',
                valueGetter: (params) => params.data?.socDate || (params.node?.id ? '-' : ''),
            },
            {
                field: 'episodeNumber',
                headerName: 'Episode',
                width: 90,
                minWidth: 90,
                filter: 'agNumberColumnFilter',
            },
            {
                field: 'admissionNumber',
                headerName: 'Admit #',
                width: 90,
                minWidth: 90,
                filter: 'agNumberColumnFilter',
            },
            {
                field: 'primaryInsuranceName',
                headerName: 'Insurance',
                flex: 1.2,
                minWidth: 200,
                filter: 'agTextColumnFilter',
                sortable: false,
                valueGetter: (params) => params.data?.primaryInsuranceName || (params.node?.id ? '-' : ''),
            },
            {
                field: 'primaryPhysician',
                headerName: 'Provider',
                flex: 1.2,
                minWidth: 210,
                filter: 'agTextColumnFilter',
                sortable: false,
                valueGetter: (params) => params.data?.primaryPhysician || (params.node?.id ? '-' : ''),
            },
        ];
    }, [t, branches, intakeDashboardEnabled]);

    const handleSelect = (event: RowSelectedEvent<PatientListItemDto>) => {
        if (event.node.isSelected()) {
            if (event.data?.status === 'INTAKE' || event.data?.status === 'REFERRAL' || event.data?.status === 'NON_ADMIT') {
                navigate(`/branch/${event.data.branchId}/admission/${event.data.admissionId}/intake`);
            } else {
                navigate(`/branch/${event.data?.branchId}/admission/${event.data?.admissionId}/patient`);
            }
        }
    };

    return (
        <Flex px={{ base: '16px', md: 0 }} pt="16px" h="100%" flexDirection="column">
            <GridFilterActionBar
                gridRef={gridApiRef}
                filterModel={gridState.filterModel}
                defaultFilterModel={DEFAULT_GRID_STATE.filterModel}
                colDefs={colDefs}
                valueMaps={{
                    status: STATUS_LABELS,
                    ...branchFilterValueMaps(branches),
                }}
            />
            <Flex justify="flex-end" flexShrink={0} pb="12px" px={{ base: 0, md: '16px' }}>
                <AccessGuard permission="intake:create">
                    <LinkButton size="xs" to="/intake" textDecoration="none">
                        <Plus /> New Patient
                    </LinkButton>
                </AccessGuard>
            </Flex>

            <Box position="relative" w="100%" h="100%" pb="24px">
                <ResponsiveGrid<PatientListItemDto>
                    key={isCompact ? 'compact-grid' : 'grid'}
                    countLabel="patient"
                    onGridReady={({ api }) => {
                        gridApiRef.current = api;
                    }}
                    onFilterChanged={(event) => saveGridState(event.api)}
                    onSortChanged={(event) => saveGridState(event.api)}
                    rowModelType="infinite"
                    datasource={datasource}
                    cacheBlockSize={50}
                    maxBlocksInCache={10}
                    rowBuffer={0}
                    infiniteInitialRowCount={1000}
                    maxConcurrentDatasourceRequests={1}
                    rowSelection="single"
                    columnDefs={colDefs}
                    suppressCellFocus
                    defaultColDef={defaultColumnDef}
                    autoSizeStrategy={isCompact ? { type: 'fitCellContents' } : { type: 'fitGridWidth' }}
                    onRowSelected={handleSelect}
                    initialState={{
                        filterModel: gridState.filterModel,
                        sortModel: gridState.sortModel,
                    }}
                />
            </Box>
        </Flex>
    );
}
